import { useCallback, useEffect, useMemo, useState } from "react";

import { useDialogs } from "@/components/shell/Dialogs";
import {
  type DeckOptions as Options,
  type DeckOptionsDraft,
  type DraftField,
  diffOptions,
  draftOf,
  getDeckOptions,
  runwayDays,
  saveDeckOptions,
} from "@/lib/deckOptions";
import type { DeckRow } from "@/lib/setup";

import { Skeleton } from "../stats/Skeleton";

/**
 * A deck's preset (日本-344), canvas https://claude.ai/artifact/KJjrBHWtep6D6RVPNS3d34.
 *
 * Two layouts of one form: `inline` opens under a Setup deck row (frames A–C), `page` is the phone's
 * drill-in (frame D). One save for the whole form, as `5g` had — a preset is shared, so a save is a
 * deliberate act rather than something a stray keystroke should commit.
 *
 * **The three ordering controls are absent, not disabled, when the preset is `orderManaged`**
 * (Alex, 26 Sep 2026). The frequency-sort job owns that queue's order and each of the three fights
 * it: insertion order re-numbers every new card on the spot, and the random gather and sort orders
 * make Anki ignore positions altogether. The server refuses them too.
 */

const LEECH_ACTIONS: [string, string][] = [
  ["tag", "タグのみ"],
  ["suspend", "休止"],
];
const NEW_MIXES: [string, string][] = [
  ["mix", "復習と混ぜる"],
  ["after", "復習の後"],
  ["before", "復習の前"],
];
const INSERT_ORDERS: [string, string][] = [
  ["due", "追加順"],
  ["random", "ランダム"],
];
/** Anki's own ja_JP, shortened where it would not fit a quarter of the panel. */
const GATHER_ORDERS: [string, string][] = [
  ["deck", "デッキ順"],
  ["deckThenRandomNotes", "デッキ順→ランダム（ノート）"],
  ["lowestPosition", "位置の昇順"],
  ["highestPosition", "位置の降順"],
  ["randomNotes", "ランダム（ノート）"],
  ["randomCards", "ランダム（カード）"],
];
const SORT_ORDERS: [string, string][] = [
  ["template", "カードタイプ順"],
  ["noSort", "集めたときの順序"],
  ["templateThenRandom", "カードタイプ順→ランダム"],
  ["randomNoteThenTemplate", "ランダム（ノート）→カードタイプ順"],
  ["randomCard", "ランダム（カード）"],
];

const leaf = (name: string) => name.split("::").pop() ?? name;

type Notice = { tone: "error"; text: string } | null;

export function DeckOptionsForm({
  deckId,
  layout,
  onSaved,
  onDirtyChange,
}: {
  deckId: number;
  layout: "inline" | "page";
  /** The deck list the save answered with, so the row's 上限 column moves at once. */
  onSaved: (decks: DeckRow[]) => void;
  onDirtyChange?: (changed: number) => void;
}) {
  const dialogs = useDialogs();
  const [saved, setSaved] = useState<Options | null>(null);
  const [draft, setDraft] = useState<DeckOptionsDraft | null>(null);
  const [notice, setNotice] = useState<Notice>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoadError(null);
    return getDeckOptions(deckId)
      .then((options) => {
        setSaved(options);
        setDraft(draftOf(options));
      })
      .catch((e: unknown) => setLoadError(e instanceof Error ? e.message : String(e)));
  }, [deckId]);

  useEffect(() => {
    setSaved(null);
    setDraft(null);
    setNotice(null);
    void load();
  }, [load]);

  const diff = useMemo(
    () => (saved && draft ? diffOptions(saved, draft) : null),
    [saved, draft],
  );
  const changed = diff?.changed.length ?? 0;
  const blocked = diff ? Object.keys(diff.invalid).length > 0 : true;

  useEffect(() => onDirtyChange?.(changed), [changed, onDirtyChange]);

  const set = (field: DraftField, value: string) =>
    setDraft((current) => (current ? { ...current, [field]: value } : current));

  const revert = () => {
    if (saved) setDraft(draftOf(saved));
    setNotice(null);
  };

  const save = async () => {
    if (!saved || !diff || changed === 0 || blocked || saving) return;
    if (diff.patch.insertOrder !== undefined) {
      // Anki re-numbers every new card on the preset the moment this saves, and nothing here can
      // put the old order back. So the one irreversible field asks, naming what moves.
      const ok = await dialogs.confirm({
        title: "並び順を変えますか？",
        body: `新規カード${saved.newOnPreset.toLocaleString()}枚の並び順が変わります。元の並び順には戻せません。`,
        confirmLabel: "保存",
        tone: "danger",
      });
      if (!ok) return;
    }
    setSaving(true);
    setNotice(null);
    try {
      const result = await saveDeckOptions(deckId, saved.presetMtime, diff.patch);
      if (result.ok) {
        setSaved(result.options);
        setDraft(draftOf(result.options));
        onSaved(result.decks);
      } else if (result.kind === "sitting") {
        setNotice({ tone: "error", text: `${leaf(result.deck)}を復習中のため保存できません。` });
      } else if (result.kind === "stale") {
        setNotice({
          tone: "error",
          text: "このプリセットは別の場所で変更されました。取り消してから開き直してください。",
        });
      } else {
        setNotice({ tone: "error", text: result.message });
      }
    } catch (e) {
      setNotice({ tone: "error", text: e instanceof Error ? e.message : String(e) });
    } finally {
      setSaving(false);
    }
  };

  const page = layout === "page";

  if (loadError) {
    return (
      <div className={page ? "p-[14px]" : "border-b border-line bg-sunken px-[18px] py-[14px] pl-[52px]"}>
        <p className="rounded-[var(--r-2)] border border-accent-line bg-accent-ground px-3 py-[9px] text-secondary text-accent-hi">
          {loadError}
        </p>
      </div>
    );
  }

  const isChanged = (field: DraftField) => diff?.changed.includes(field) ?? false;
  const badToken = (field: DraftField) => diff?.invalid[field];

  const runway =
    saved && draft && /^\d+$/.test(draft.newPerDay.trim())
      ? runwayDays(saved.unseen, Number(draft.newPerDay))
      : null;

  const sections = [
    {
      key: "limits",
      title: "一日の上限",
      body:
        saved && draft ? (
          <>
            <div className={page ? "grid grid-cols-2 gap-[11px]" : "contents"}>
              <NumberField
                id="newPerDay"
                label={page ? "新規カード／日" : "新規カード"}
                unit={page ? undefined : "枚／日"}
                value={draft.newPerDay}
                changed={isChanged("newPerDay")}
                bad={badToken("newPerDay") !== undefined}
                page={page}
                onChange={(v) => set("newPerDay", v)}
              />
              <NumberField
                id="reviewsPerDay"
                label={page ? "復習／日" : "復習"}
                unit={page ? undefined : "枚／日"}
                value={draft.reviewsPerDay}
                changed={isChanged("reviewsPerDay")}
                bad={badToken("reviewsPerDay") !== undefined}
                page={page}
                onChange={(v) => set("reviewsPerDay", v)}
              />
            </div>
            <span data-check="deck-options.runway" className="text-meta text-ink-4">
              新規 <Figure>{saved.unseen.toLocaleString()}</Figure>枚
              {runway !== null && (
                <>
                  {" · "}このペースで<Figure>{runway.toLocaleString()}</Figure>日
                </>
              )}
            </span>
          </>
        ) : (
          <SkeletonFields count={2} short />
        ),
    },
    {
      key: "new",
      title: "新規カード",
      body:
        saved && draft ? (
          <>
            <StepsField
              id="learnSteps"
              label="習得ステップ"
              value={draft.learnSteps}
              changed={isChanged("learnSteps")}
              bad={badToken("learnSteps")}
              hint={page ? undefined : "m・h・d をスペース区切り"}
              page={page}
              onChange={(v) => set("learnSteps", v)}
            />
            {saved.orderManaged ? (
              <div
                data-check="deck-options.managed"
                className="border-t border-dashed border-line-strong pt-[10px] text-meta text-ink-4"
              >
                <b className="font-semibold text-ink-3">並び順</b> — 頻度順ジョブが決めます
              </div>
            ) : (
              draft.insertOrder !== null && (
                <Segmented
                  id="insertOrder"
                  label="配置順序"
                  options={INSERT_ORDERS}
                  value={draft.insertOrder}
                  changed={isChanged("insertOrder")}
                  page={page}
                  onChange={(v) => set("insertOrder", v)}
                />
              )
            )}
          </>
        ) : (
          <SkeletonFields count={1} />
        ),
    },
    {
      key: "lapses",
      title: "忘却",
      body:
        saved && draft ? (
          <>
            <div className={page ? "grid grid-cols-2 gap-[11px]" : "contents"}>
              <StepsField
                id="relearnSteps"
                label="再習得ステップ"
                value={draft.relearnSteps}
                changed={isChanged("relearnSteps")}
                bad={badToken("relearnSteps")}
                page={page}
                onChange={(v) => set("relearnSteps", v)}
              />
              <NumberField
                id="leechThreshold"
                label={page ? "苦戦の回数" : "苦戦とする忘却回数"}
                value={draft.leechThreshold}
                changed={isChanged("leechThreshold")}
                bad={badToken("leechThreshold") !== undefined}
                page={page}
                onChange={(v) => set("leechThreshold", v)}
              />
            </div>
            <Segmented
              id="leechAction"
              label="苦戦カード"
              options={LEECH_ACTIONS}
              value={draft.leechAction}
              changed={isChanged("leechAction")}
              page={page}
              onChange={(v) => set("leechAction", v)}
              hint={
                <>
                  leech タグ <Figure>{saved.leechNotes.toLocaleString()}</Figure>件
                </>
              }
            />
          </>
        ) : (
          <SkeletonFields count={2} />
        ),
    },
    {
      key: "order",
      title: "表示順序",
      body:
        saved && draft ? (
          <>
            {!saved.orderManaged && draft.gatherOrder !== null && (
              <SelectField
                id="gatherOrder"
                label="新規カードを集める順序"
                options={GATHER_ORDERS}
                value={draft.gatherOrder}
                changed={isChanged("gatherOrder")}
                page={page}
                onChange={(v) => set("gatherOrder", v)}
              />
            )}
            {!saved.orderManaged && draft.sortOrder !== null && (
              <SelectField
                id="sortOrder"
                label="集めたカードの並べ方"
                options={SORT_ORDERS}
                value={draft.sortOrder}
                changed={isChanged("sortOrder")}
                page={page}
                onChange={(v) => set("sortOrder", v)}
              />
            )}
            <Segmented
              id="newMix"
              label="新規カードの表示"
              options={NEW_MIXES}
              value={draft.newMix}
              changed={isChanged("newMix")}
              page={page}
              onChange={(v) => set("newMix", v)}
            />
          </>
        ) : (
          <SkeletonFields count={1} />
        ),
    },
  ];

  const presetLine = saved ? (
    <div
      data-check="deck-options.preset"
      className={`flex flex-wrap items-center gap-2 text-secondary text-ink-3 ${page ? "" : "pb-[14px]"}`}
    >
      プリセット <b className="font-semibold text-ink">{saved.preset}</b>
      <span className="text-ink-4">·</span>
      {!page && "使用中"}
      <Chip here>{leaf(saved.deck)}</Chip>
      {saved.sharedWith.map((name) => (
        <Chip key={name}>{leaf(name)}</Chip>
      ))}
    </div>
  ) : (
    <div className={page ? "" : "pb-[14px]"}>
      <Skeleton width={220} height={14} />
    </div>
  );

  const noticeLine = notice && (
    <p
      role="status"
      data-check="deck-options.notice"
      className="rounded-[var(--r-2)] border border-accent-line bg-accent-ground px-3 py-[9px] text-secondary text-accent-hi"
    >
      {notice.text}
    </p>
  );

  const state = saved ? (
    <span className={`text-secondary ${changed ? "text-ink-3" : "text-ink-4"}`}>
      {changed ? `変更 ${changed}件` : "変更なし"}
    </span>
  ) : null;

  const buttons = (
    <>
      <button
        type="button"
        data-check="deck-options.revert"
        disabled={!saved || changed === 0 || saving}
        onClick={revert}
        className={`rounded-[var(--r-2)] border border-line-strong bg-void font-semibold text-ink-2 disabled:opacity-40 ${
          page ? "min-h-[48px] flex-1 rounded-[var(--r-3)] bg-panel text-[16px]/[1]" : "px-[11px] py-[6px] text-[14px]/[1]"
        }`}
      >
        取り消す
      </button>
      <button
        type="button"
        data-check="deck-options.save"
        disabled={!saved || changed === 0 || blocked || saving}
        onClick={() => void save()}
        className={`rounded-[var(--r-2)] border border-transparent bg-ink font-semibold text-void disabled:opacity-40 ${
          page ? "min-h-[48px] flex-1 rounded-[var(--r-3)] text-[16px]/[1]" : "px-[11px] py-[6px] text-[14px]/[1]"
        }`}
      >
        保存
      </button>
    </>
  );

  if (page) {
    return (
      <div data-check="deck-options" className="flex min-h-0 flex-1 flex-col">
        <div className="flex min-h-0 flex-1 flex-col gap-[11px] overflow-auto p-[14px]">
          {noticeLine}
          {presetLine}
          {sections.map((section) => (
            <section
              key={section.key}
              className="flex flex-col gap-3 rounded-[var(--r-3)] border border-line bg-panel p-[13px]"
            >
              <Eyebrow>{section.title}</Eyebrow>
              {section.body}
            </section>
          ))}
        </div>
        <div className="flex shrink-0 gap-[10px] border-t border-line bg-panel px-[14px] pt-3 pb-[18px]">
          {buttons}
        </div>
      </div>
    );
  }

  return (
    <div
      data-check="deck-options"
      className="border-b border-line bg-sunken pt-4 pr-[18px] pl-[52px]"
    >
      {noticeLine && <div className="pb-[14px]">{noticeLine}</div>}
      {presetLine}
      <div className="grid grid-cols-4 border-t border-line-soft">
        {sections.map((section, index) => (
          <section
            key={section.key}
            className={`flex min-w-0 flex-col gap-3 pt-[14px] pr-[18px] pb-4 ${
              index > 0 ? "border-l border-line-soft pl-[18px]" : ""
            }`}
          >
            <Eyebrow>{section.title}</Eyebrow>
            {section.body}
          </section>
        ))}
      </div>
      <div className="flex items-center gap-[10px] border-t border-line-soft pt-3 pb-[14px]">
        {state}
        <span className="flex-1" />
        {buttons}
      </div>
    </div>
  );
}

function Eyebrow({ children }: { children: React.ReactNode }) {
  return <span className="text-eyebrow font-semibold tracking-[.1em] text-ink-3">{children}</span>;
}

function Figure({ children }: { children: React.ReactNode }) {
  return <span className="font-mono text-ink-2">{children}</span>;
}

function Chip({ here, children }: { here?: boolean; children: React.ReactNode }) {
  return (
    <span
      className={`inline-flex items-center rounded-[var(--r-2)] px-2 py-px text-secondary ${
        here ? "bg-raised text-ink shadow-[inset_0_0_0_1px_var(--line-strong)]" : "bg-line-soft text-ink-2"
      }`}
    >
      {children}
    </span>
  );
}

function fieldBorder(changed: boolean, bad: boolean) {
  if (bad) return "border-accent-line shadow-[inset_0_0_0_1px_var(--accent-line)]";
  return changed ? "border-ink-3" : "border-line-strong";
}

function inputClass(page: boolean, changed: boolean, bad: boolean, short: boolean) {
  return [
    "rounded-[var(--r-2)] border bg-void font-mono text-ink",
    page ? "w-full px-[10px] py-[10px] text-[16px]/[1.2]" : "px-2 py-[6px] text-[14px]/[1.2]",
    !page && short ? "w-[96px]" : "w-full",
    fieldBorder(changed, bad),
  ].join(" ");
}

function Label({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <label htmlFor={`deck-options-${id}`} className="text-field-label text-ink-4">
      {children}
    </label>
  );
}

function NumberField({
  id,
  label,
  unit,
  value,
  changed,
  bad,
  page,
  onChange,
}: {
  id: DraftField;
  label: string;
  unit?: string;
  value: string;
  changed: boolean;
  bad: boolean;
  page: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <Label id={id}>{label}</Label>
      <input
        id={`deck-options-${id}`}
        data-check={`deck-options.${id}`}
        inputMode="numeric"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={inputClass(page, changed, bad, true)}
      />
      {unit && <span className="text-meta text-ink-4">{unit}</span>}
    </div>
  );
}

function StepsField({
  id,
  label,
  value,
  changed,
  bad,
  hint,
  page,
  onChange,
}: {
  id: DraftField;
  label: string;
  value: string;
  changed: boolean;
  bad: string | undefined;
  hint?: string;
  page: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <Label id={id}>{label}</Label>
      <input
        id={`deck-options-${id}`}
        data-check={`deck-options.${id}`}
        value={value}
        spellCheck={false}
        onChange={(e) => onChange(e.target.value)}
        className={inputClass(page, changed, bad !== undefined, false)}
      />
      {bad !== undefined ? (
        <span data-check={`deck-options.${id}.error`} className="text-meta text-accent-hi">
          「{bad}」はステップとして読めません
        </span>
      ) : (
        hint && <span className="text-meta text-ink-4">{hint}</span>
      )}
    </div>
  );
}

function Segmented({
  id,
  label,
  options,
  value,
  changed,
  page,
  onChange,
  hint,
}: {
  id: DraftField;
  label: string;
  options: [string, string][];
  value: string;
  changed: boolean;
  page: boolean;
  onChange: (value: string) => void;
  hint?: React.ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span id={`deck-options-${id}-label`} className="text-field-label text-ink-4">
        {label}
      </span>
      <div
        role="radiogroup"
        aria-labelledby={`deck-options-${id}-label`}
        data-check={`deck-options.${id}`}
        className={`inline-flex w-fit flex-wrap gap-[2px] rounded-[var(--r-2)] border bg-raised p-[2px] ${
          changed ? "border-ink-3" : "border-line-strong"
        }`}
      >
        {options.map(([key, text]) => {
          const on = key === value;
          return (
            <button
              key={key}
              type="button"
              role="radio"
              aria-checked={on}
              data-value={key}
              onClick={() => onChange(key)}
              className={`rounded-[var(--r-2)] text-[14px]/[1] ${page ? "px-[11px] py-[10px]" : "px-[9px] py-[6px]"} ${
                on ? "bg-ink font-semibold text-void" : "text-ink-3"
              }`}
            >
              {text}
            </button>
          );
        })}
      </div>
      {hint && <span className="text-meta text-ink-4">{hint}</span>}
    </div>
  );
}

function SelectField({
  id,
  label,
  options,
  value,
  changed,
  page,
  onChange,
}: {
  id: DraftField;
  label: string;
  options: [string, string][];
  value: string;
  changed: boolean;
  page: boolean;
  onChange: (value: string) => void;
}) {
  // A member a future `anki==` bump adds arrives as `unknown:N`; it is shown as itself and left
  // alone rather than silently rewritten to whatever the first option is.
  const known = options.some(([key]) => key === value);
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <Label id={id}>{label}</Label>
      <select
        id={`deck-options-${id}`}
        data-check={`deck-options.${id}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full appearance-none rounded-[var(--r-2)] border bg-void bg-[url("data:image/svg+xml,%3Csvg_xmlns='http://www.w3.org/2000/svg'_width='10'_height='6'%3E%3Cpath_d='M1_1l4_4_4-4'_fill='none'_stroke='%238e8c86'_stroke-width='1.4'/%3E%3C/svg%3E")] bg-[position:right_8px_center] bg-no-repeat py-[6px] pr-[26px] pl-2 text-ink ${
          page ? "text-[16px]/[1.2]" : "text-[14px]/[1.2]"
        } ${changed ? "border-ink-3" : "border-line-strong"}`}
      >
        {!known && <option value={value}>{value}</option>}
        {options.map(([key, text]) => (
          <option key={key} value={key}>
            {text}
          </option>
        ))}
      </select>
    </div>
  );
}

function SkeletonFields({ count, short }: { count: number; short?: boolean }) {
  return (
    <>
      {Array.from({ length: count }, (_, index) => (
        <Skeleton key={index} height={30} width={short ? 96 : "100%"} />
      ))}
    </>
  );
}
